import { Money } from "@despejo/domain";
import { computeIva, type IvaInput, type IvaResult } from "./iva.js";
import { reconcile, type Candidate, type DiffCause, type ReconciliationResult, type UserExplanation } from "./reconcile.js";

/**
 * Filing package (plan §40 Phase 4, DES-72): the evidence bundle for one
 * IVA_GENERAL period. Pure: (ledger facts, draft, explanations, asOf) ->
 * validations + a deterministic JSON document whose SHA-256 is stored with the
 * filing. A package with blocking issues must not be marked as filed.
 */

export const FILING_PACKAGE_FORMAT = "filing-package@1.0.0";

/** Form 150 figures reconciled box by box against the TRIBU-CR draft. */
export const RECONCILED_FIELDS = ["totalDebit", "totalCredit", "payable", "creditCarryForward"] as const;
export type ReconciledField = (typeof RECONCILED_FIELDS)[number];

export interface PackageObligation {
  id: string;
  taxpayerId: string;
  obligationCode: string;
  periodKey: string;
  /** CR calendar date (YYYY-MM-DD). */
  dueDate: string;
  provisional: boolean;
  filingStatus: string;
}

export interface PackageTransaction {
  id: string;
  label: string;
  direction: "INCOME" | "EXPENSE";
  reviewStatus: "NEEDS_REVIEW" | "CLASSIFIED" | "CONFIRMED" | string;
  currency: string;
  exchangeRate: string | null;
  receiptId: string | null;
}

export interface PackageReceipt {
  id: string;
  clave: string;
  direction: "ISSUED" | "RECEIVED";
  docType: string;
  issueDate: string;
  currency: string;
  total: string;
  totalTax: string;
  haciendaStatus: "ACCEPTED" | "REJECTED" | "UNKNOWN" | string;
  acceptanceStatus: string;
  /** CR calendar date, null when not tracked. */
  acceptanceDueDate: string | null;
  /** The ledger transaction that counts this receipt in the period. */
  transactionId: string;
}

export interface FilingPackageInput {
  obligation: PackageObligation;
  /** CR calendar date the package is evaluated on (deadline checks). */
  asOf: string;
  iva: IvaInput;
  /** Lines the ledger mapping could not classify (ledgerToIvaInput issues). */
  mappingIssues: readonly { ref: string; message: string }[];
  transactions: readonly PackageTransaction[];
  receipts: readonly PackageReceipt[];
  /** TRIBU-CR prefilled draft (Form 150), null when not captured yet. */
  draft: Record<string, string> | null;
  userExplanations: readonly UserExplanation[];
}

export type IssueCode =
  | "UNCLASSIFIED_LINE"
  | "TRANSACTION_NEEDS_REVIEW"
  | "MISSING_EXCHANGE_RATE"
  | "REJECTED_RECEIPT_COUNTED"
  | "ACCEPTANCE_OVERDUE"
  | "ENGINE_EXCEPTION"
  | "DRAFT_MISSING"
  | "DIFF_UNEXPLAINED"
  | "PROVISIONAL_DEADLINE"
  | "CREDIT_DENIED"
  | "ACCEPTANCE_PENDING"
  | "RECEIPT_REJECTED_BY_TAXPAYER"
  | "HACIENDA_STATUS_UNKNOWN"
  | "LATE_FILING"
  | "NIL_RETURN"
  | "AMENDMENT";

export interface ValidationIssue {
  code: IssueCode;
  severity: "BLOCKING" | "WARNING";
  ref: string | null;
  message: string;
}

export interface Validation {
  ok: boolean;
  blocking: ValidationIssue[];
  warnings: ValidationIssue[];
}

export interface PackageReceiptEntry {
  clave: string;
  direction: string;
  docType: string;
  issueDate: string;
  currency: string;
  total: string;
  totalTax: string;
  haciendaStatus: string;
  acceptanceStatus: string;
  transactionId: string;
}

export interface FilingPackage {
  format: typeof FILING_PACKAGE_FORMAT;
  engine: string;
  obligation: PackageObligation;
  asOf: string;
  /** SHA-256 of the canonical input without asOf: identifies the data, not the day it was evaluated. */
  inputSha256: string;
  /** Form 150 figures as stored with the filing (decimal strings, 2 dp). */
  figures: Record<string, string>;
  calculation: IvaResult;
  draft: Record<string, string> | null;
  /** User explanations applied to the reconciliation (kept so the package can be rebuilt). */
  explanations: UserExplanation[];
  reconciliation: ReconciliationResult | null;
  receipts: PackageReceiptEntry[];
  validation: Validation;
}

export interface BuiltFilingPackage {
  pkg: FilingPackage;
  /** Canonical JSON (sorted keys, no whitespace): the bytes that are hashed and stored. */
  json: string;
  sha256: string;
}

// --------------------------------------------------------------- canonical JSON

/** JSON with object keys sorted recursively; arrays keep their order. Deterministic. */
export function canonicalJson(value: unknown): string {
  if (value === null || value === undefined) return "null";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new RangeError("non-finite number in package");
    return JSON.stringify(value);
  }
  if (typeof value === "string" || typeof value === "boolean") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((v) => canonicalJson(v)).join(",")}]`;
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj)
      .filter((k) => obj[k] !== undefined)
      .sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`).join(",")}}`;
  }
  throw new TypeError(`cannot serialise ${typeof value}`);
}

export async function sha256Hex(text: string): Promise<string> {
  const buf = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ------------------------------------------------------------------ figures

/** Form 150 figures kept with the filing and diffed by amendments. */
export function form150Figures(r: IvaResult): Record<string, string> {
  return {
    salesExempt: r.salesExempt,
    salesExport: r.salesExport,
    salesNonSubject: r.salesNonSubject,
    totalDebit: r.totalDebit,
    creditFull: r.creditFull,
    creditProrrata: r.creditProrrata,
    creditNotAllowed: r.creditNotAllowed,
    totalCredit: r.totalCredit,
    cardRetentions: r.cardRetentions,
    priorCreditBalance: r.priorCreditBalance,
    payable: r.payable,
    creditCarryForward: r.creditCarryForward,
  };
}

/** Explanations the platform can prove: IVA it refused that Hacienda's draft likely credits. */
export function candidatesFromTrace(r: IvaResult): Candidate[] {
  return r.trace
    .filter((t) => t.bucket === "credit:not_allowed")
    .map((t) => {
      const note = t.note ?? "";
      const cause: DiffCause = /aceptación/.test(note) ? "UNACCEPTED_INVOICE" : /personal/i.test(note) ? "PERSONAL_PURCHASE" : "OTHER";
      return { cause, field: "totalCredit", amount: Money.of(t.amount).neg().toString(), refs: [t.ref], note };
    });
}

export function reconcileWithDraft(r: IvaResult, draft: Record<string, string> | null, explanations: readonly UserExplanation[] = []): ReconciliationResult | null {
  if (!draft) return null;
  const keys = RECONCILED_FIELDS.filter((k) => draft[k] !== undefined && draft[k] !== null && draft[k] !== "");
  if (!keys.length) return null;
  const platform = Object.fromEntries(keys.map((k) => [k, r[k]]));
  const drafted = Object.fromEntries(keys.map((k) => [k, draft[k]!]));
  return reconcile(platform, drafted, candidatesFromTrace(r), explanations);
}

// --------------------------------------------------------------- validation

const ENGINE_BLOCKING = new Set(["MISSING_RATE", "PRORRATA_UNKNOWN", "UNKNOWN_IVA_TREATMENT"]);
const FIELD_LABEL: Record<string, string> = {
  totalDebit: "Débito fiscal",
  totalCredit: "Crédito fiscal",
  payable: "Impuesto por pagar",
  creditCarryForward: "Saldo a favor",
};

export function validateFilingPackage(input: FilingPackageInput, result: IvaResult, recon: ReconciliationResult | null): Validation {
  const blocking: ValidationIssue[] = [];
  const warnings: ValidationIssue[] = [];
  const block = (code: IssueCode, ref: string | null, message: string) => blocking.push({ code, severity: "BLOCKING", ref, message });
  const warn = (code: IssueCode, ref: string | null, message: string) => warnings.push({ code, severity: "WARNING", ref, message });

  // Ledger completeness.
  for (const i of input.mappingIssues) block("UNCLASSIFIED_LINE", i.ref, i.message);
  for (const t of input.transactions) {
    if (t.reviewStatus === "NEEDS_REVIEW") block("TRANSACTION_NEEDS_REVIEW", t.id, `${t.label}: movimiento sin clasificar`);
    if (t.currency !== "CRC" && (t.exchangeRate === null || t.exchangeRate === "" || Money.of(t.exchangeRate).isZero()))
      block("MISSING_EXCHANGE_RATE", t.id, `${t.label}: monto en ${t.currency} sin tipo de cambio`);
  }

  // Electronic receipts counted in the period.
  for (const r of input.receipts) {
    if (r.haciendaStatus === "REJECTED") block("REJECTED_RECEIPT_COUNTED", r.clave, `Comprobante ${r.clave} rechazado por Hacienda y aún incluido en el periodo`);
    else if (r.haciendaStatus !== "ACCEPTED") warn("HACIENDA_STATUS_UNKNOWN", r.clave, `Comprobante ${r.clave} sin respuesta de Hacienda confirmada`);
    if (r.direction === "RECEIVED" && r.acceptanceStatus === "PENDING") {
      if (r.acceptanceDueDate && r.acceptanceDueDate < input.asOf)
        block("ACCEPTANCE_OVERDUE", r.clave, `Comprobante ${r.clave}: plazo de aceptación vencido el ${r.acceptanceDueDate} sin decisión`);
      else
        warn("ACCEPTANCE_PENDING", r.clave, `Comprobante ${r.clave}: acéptelo${r.acceptanceDueDate ? ` antes del ${r.acceptanceDueDate}` : ""} para acreditar su IVA`);
    }
    if (r.direction === "RECEIVED" && r.acceptanceStatus === "REJECTED")
      warn("RECEIPT_REJECTED_BY_TAXPAYER", r.clave, `Comprobante ${r.clave} rechazado por el contribuyente pero incluido en el periodo (no genera crédito)`);
  }

  // Engine exceptions.
  for (const e of result.exceptions) {
    if (ENGINE_BLOCKING.has(e.code)) block("ENGINE_EXCEPTION", e.ref, e.message);
    else if (e.code === "ACCEPTANCE_PENDING") continue; // already warned once per receipt above
    else warn("CREDIT_DENIED", e.ref, e.message);
  }

  // Reconciliation against the TRIBU-CR draft.
  if (!recon) block("DRAFT_MISSING", null, "Falta el borrador precargado de TRIBU-CR para comparar casilla por casilla");
  else
    for (const f of recon.fields)
      if (f.status === "UNEXPLAINED") block("DIFF_UNEXPLAINED", f.field, `${FIELD_LABEL[f.field] ?? f.field}: diferencia sin explicar de ${f.unexplained} frente al borrador`);

  // Non-blocking context.
  if (input.obligation.provisional) warn("PROVISIONAL_DEADLINE", null, "La fecha de vencimiento es provisional: confírmela en TRIBU-CR");
  if (input.asOf > input.obligation.dueDate) warn("LATE_FILING", null, `Vencía el ${input.obligation.dueDate}: la presentación será tardía y puede generar multa`);
  if (result.isNil) warn("NIL_RETURN", null, "Declaración en cero: no hay movimientos en el periodo");
  if (!["NOT_FILED", "NOT_APPLICABLE", "WAIVED"].includes(input.obligation.filingStatus))
    warn("AMENDMENT", null, "Este periodo ya fue presentado: registrarlo de nuevo será una rectificativa");

  const byRef = (a: ValidationIssue, b: ValidationIssue) => a.code.localeCompare(b.code) || (a.ref ?? "").localeCompare(b.ref ?? "") || a.message.localeCompare(b.message);
  blocking.sort(byRef);
  warnings.sort(byRef);
  return { ok: blocking.length === 0, blocking, warnings };
}

// ------------------------------------------------------------------- build

const byKey = <T>(key: (x: T) => string) => (a: T, b: T) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0);

/** Order-independent input: the same ledger always yields the same package bytes. */
export function normalizeInput(input: FilingPackageInput): FilingPackageInput {
  return {
    ...input,
    iva: {
      ...input.iva,
      sales: [...input.iva.sales].sort(byKey((s) => s.ref)),
      purchases: [...input.iva.purchases].sort(byKey((p) => p.ref)),
    },
    mappingIssues: [...input.mappingIssues].sort(byKey((i) => `${i.ref}\u0000${i.message}`)),
    transactions: [...input.transactions].sort(byKey((t) => t.id)),
    receipts: [...input.receipts].sort(byKey((r) => `${r.clave}\u0000${r.direction}`)),
    userExplanations: [...input.userExplanations],
  };
}

export async function buildFilingPackage(raw: FilingPackageInput): Promise<BuiltFilingPackage> {
  const input = normalizeInput(raw);
  const calculation = computeIva(input.iva);
  const reconciliation = reconcileWithDraft(calculation, input.draft, input.userExplanations);
  const validation = validateFilingPackage(input, calculation, reconciliation);
  const pkg: FilingPackage = {
    format: FILING_PACKAGE_FORMAT,
    engine: calculation.engine,
    obligation: input.obligation,
    asOf: input.asOf,
    inputSha256: await sha256Hex(canonicalJson({ ...input, asOf: undefined })),
    figures: form150Figures(calculation),
    calculation,
    draft: input.draft,
    explanations: [...input.userExplanations],
    reconciliation,
    receipts: input.receipts.map((r) => ({
      clave: r.clave,
      direction: r.direction,
      docType: r.docType,
      issueDate: r.issueDate,
      currency: r.currency,
      total: r.total,
      totalTax: r.totalTax,
      haciendaStatus: r.haciendaStatus,
      acceptanceStatus: r.acceptanceStatus,
      transactionId: r.transactionId,
    })),
    validation,
  };
  const json = canonicalJson(pkg);
  return { pkg, json, sha256: await sha256Hex(json) };
}

// ---------------------------------------------------------------- checklist

export interface FilingChecklistItem {
  key: string;
  label: string;
  status: "OK" | "WARNING" | "BLOCKING";
  issues: ValidationIssue[];
}

/** The printable checklist: every check is listed, passed or not (plan §40 Phase 4). */
export const FILING_CHECKLIST: readonly { key: string; label: string; codes: readonly IssueCode[] }[] = [
  { key: "classified", label: "Todos los movimientos del periodo están clasificados", codes: ["UNCLASSIFIED_LINE", "TRANSACTION_NEEDS_REVIEW"] },
  { key: "fx", label: "Montos en moneda extranjera con tipo de cambio", codes: ["MISSING_EXCHANGE_RATE"] },
  { key: "hacienda", label: "Comprobantes aceptados por Hacienda", codes: ["REJECTED_RECEIPT_COUNTED", "HACIENDA_STATUS_UNKNOWN"] },
  { key: "acceptance", label: "Aceptaciones de comprobantes recibidos al día", codes: ["ACCEPTANCE_OVERDUE", "ACCEPTANCE_PENDING", "RECEIPT_REJECTED_BY_TAXPAYER"] },
  { key: "calculation", label: "Cálculo del formulario 150 sin excepciones", codes: ["ENGINE_EXCEPTION", "CREDIT_DENIED"] },
  { key: "reconciliation", label: "Conciliado casilla por casilla con el borrador de TRIBU-CR", codes: ["DRAFT_MISSING", "DIFF_UNEXPLAINED"] },
  { key: "deadline", label: "Fecha de vencimiento confirmada y en plazo", codes: ["PROVISIONAL_DEADLINE", "LATE_FILING"] },
  { key: "context", label: "Otras observaciones", codes: ["NIL_RETURN", "AMENDMENT"] },
];

export function filingChecklist(v: Validation): FilingChecklistItem[] {
  const all = [...v.blocking, ...v.warnings];
  return FILING_CHECKLIST.map((c) => {
    const issues = all.filter((i) => c.codes.includes(i.code));
    const status = issues.some((i) => i.severity === "BLOCKING") ? "BLOCKING" : issues.length ? "WARNING" : "OK";
    return { key: c.key, label: c.label, status, issues };
  });
}
